import React, { useState, useEffect } from 'react';
import { Save, DollarSign, AlertCircle, Eye, RefreshCw, Zap, Image, Sparkles, Palette, GraduationCap, Edit2, X, Video, Mic } from 'lucide-react';

export default function PriceListAdmin() {
  const [pricingSettings, setPricingSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form state - ALLE SNAP-FELDER
  const [snapsPerEuro, setSnapsPerEuro] = useState(1.0);
  const [snapsPerTraining, setSnapsPerTraining] = useState(50.0);
  const [snapsPerShot, setSnapsPerShot] = useState(1.0);
  const [snapsPerRemix, setSnapsPerRemix] = useState(1.0);
  const [snapsPerModify, setSnapsPerModify] = useState(1.0);
  const [snapsPerVideo, setSnapsPerVideo] = useState(3.0);  // 🎬 NEU: Video-Generation
  const [snapsPerLipsync, setSnapsPerLipsync] = useState(1.0);  // 🎙️ NEU: Lipsync
  
  useEffect(() => {
    loadPricing();
  }, []);
  
  const loadPricing = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('snapshot_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/pricing/admin/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Fehler beim Laden der Preise');
      }
      
      const data = await response.json();
      
      setPricingSettings(data);
      setSnapsPerEuro(data.snaps_per_euro || 1.0);
      setSnapsPerTraining(data.snaps_per_avatar || 50.0);
      setSnapsPerShot(data.snaps_per_shot || 1.0);
      setSnapsPerRemix(data.snaps_per_remix || 1.0);
      setSnapsPerModify(data.snaps_per_modify || 1.0);
      setSnapsPerVideo(data.snaps_per_video || 3.0);  // 🎬 NEU: Video-Generation
      setSnapsPerLipsync(data.snaps_per_lipsync || 1.0);  // 🎙️ NEU: Lipsync
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading pricing:', err);
      setError('Fehler beim Laden der Preise');
      setLoading(false);
    }
  };
  
  const savePricing = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const token = localStorage.getItem('snapshot_token');
      
      const payload = {
        snaps_per_euro: parseFloat(snapsPerEuro),
        snaps_per_avatar: parseFloat(snapsPerTraining),
        snaps_per_shot: parseFloat(snapsPerShot),
        snaps_per_remix: parseFloat(snapsPerRemix),
        snaps_per_modify: parseFloat(snapsPerModify),
        snaps_per_video: parseFloat(snapsPerVideo),  // 🎬 NEU: Video-Generation
        snaps_per_lipsync: parseFloat(snapsPerLipsync)  // 🎙️ NEU: Lipsync
      };
      
      console.log('💾 Speichere Pricing Settings:', payload);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/pricing/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ API Error:', errorData);
        throw new Error(errorData.detail || 'Update fehlgeschlagen');
      }
      
      const result = await response.json();
      console.log('✅ API Response:', result);
      
      await loadPricing(); // Reload to confirm
      setSuccess('✅ Preise erfolgreich aktualisiert!');
      setSaving(false);
      setEditing(false); // Bearbeitungsmodus beenden
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      console.error('Error saving pricing:', err);
      setError('Fehler beim Speichern: ' + err.message);
      setSaving(false);
    }
  };
  
  // Calculate price in EUR based on SNAPs
  const calculatePrice = (snaps) => {
    if (snapsPerEuro === 0) return '0.00';
    return (snaps / snapsPerEuro).toFixed(2);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 to-black">
        <div className="text-white text-xl">Lade Preise...</div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <DollarSign className="w-10 h-10 text-green-400" />
            Globale Preisliste (Avatar + Vehicle)
          </h1>
          <p className="text-gray-400">Dezimal-SNAPs erlaubt • Preise unter 1 € möglich</p>
        </div>
        
        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300">{error}</span>
          </div>
        )}
        
        {/* Success Alert */}
        {success && (
          <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-xl flex items-center gap-3">
            <Zap className="w-5 h-5 text-green-400" />
            <span className="text-green-300">{success}</span>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* LEFT: Editor */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">SNAP-Kosten bearbeiten</h2>
              
              {!editing ? (
                <button
                  onClick={() => setEditing(true)}
                  className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Bearbeiten
                </button>
              ) : (
                <button
                  onClick={() => {
                    loadPricing(); // Reset values
                    setEditing(false);
                  }}
                  className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Abbrechen
                </button>
              )}
            </div>
            
            {/* Conversion Rate Info */}
            <div className="mb-8 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <p className="text-blue-300 text-sm font-medium mb-1">Umrechnungskurs</p>
              <p className="text-blue-100 text-xl font-bold">1 € = {snapsPerEuro} SNAP{snapsPerEuro !== 1 ? 's' : ''}</p>
            </div>
            
            {/* Training (Avatar + Vehicle) */}
            <div className="mb-6 p-5 bg-purple-500/10 border border-purple-500/30 rounded-xl">
              <label className="flex items-center gap-3 text-sm font-medium text-purple-300 mb-3">
                <GraduationCap className="w-5 h-5" />
                Training (Avatar + Vehicle)
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max="100"
                inputMode="decimal"
                value={snapsPerTraining}
                onChange={(e) => {
                  const val = e.target.value.replace(',', '.');
                  setSnapsPerTraining(parseFloat(val) || 0.01);
                }}
                disabled={!editing}
                className="w-full px-5 py-4 bg-white/10 backdrop-blur-xl border border-purple-500/30 rounded-xl text-white font-bold text-2xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div className="flex items-center justify-between mt-3">
                <p className="text-purple-300/70 text-xs">
                  {snapsPerTraining} SNAP{snapsPerTraining !== 1 ? 's' : ''}
                </p>
                <p className="text-purple-200 text-sm font-bold">
                  = €{calculatePrice(snapsPerTraining)}
                </p>
              </div>
            </div>
            
            {/* Shot / Standard-Bild */}
            <div className="mb-6 p-5 bg-green-500/10 border border-green-500/30 rounded-xl">
              <label className="flex items-center gap-3 text-sm font-medium text-green-300 mb-3">
                <Image className="w-5 h-5" />
                Shot / Standard-Bild (Avatar + Vehicle)
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max="100"
                inputMode="decimal"
                value={snapsPerShot}
                onChange={(e) => {
                  const val = e.target.value.replace(',', '.');
                  setSnapsPerShot(parseFloat(val) || 0.01);
                }}
                disabled={!editing}
                className="w-full px-5 py-4 bg-white/10 backdrop-blur-xl border border-green-500/30 rounded-xl text-white font-bold text-2xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div className="flex items-center justify-between mt-3">
                <p className="text-green-300/70 text-xs">
                  {snapsPerShot} SNAP{snapsPerShot !== 1 ? 's' : ''}
                </p>
                <p className="text-green-200 text-sm font-bold">
                  = €{calculatePrice(snapsPerShot)}
                </p>
              </div>
            </div>
            
            {/* Remix */}
            <div className="mb-6 p-5 bg-cyan-500/10 border border-cyan-500/30 rounded-xl">
              <label className="flex items-center gap-3 text-sm font-medium text-cyan-300 mb-3">
                <Sparkles className="w-5 h-5" />
                Remix (Avatar + Vehicle)
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max="100"
                inputMode="decimal"
                value={snapsPerRemix}
                onChange={(e) => {
                  const val = e.target.value.replace(',', '.');
                  setSnapsPerRemix(parseFloat(val) || 0.01);
                }}
                disabled={!editing}
                className="w-full px-5 py-4 bg-white/10 backdrop-blur-xl border border-cyan-500/30 rounded-xl text-white font-bold text-2xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div className="flex items-center justify-between mt-3">
                <p className="text-cyan-300/70 text-xs">
                  {snapsPerRemix} SNAP{snapsPerRemix !== 1 ? 's' : ''}
                </p>
                <p className="text-cyan-200 text-sm font-bold">
                  = €{calculatePrice(snapsPerRemix)}
                </p>
              </div>
            </div>
            
            {/* Modify */}
            <div className="mb-6 p-5 bg-orange-500/10 border border-orange-500/30 rounded-xl">
              <label className="flex items-center gap-3 text-sm font-medium text-orange-300 mb-3">
                <Palette className="w-5 h-5" />
                Modify (Avatar + Vehicle)
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max="100"
                inputMode="decimal"
                value={snapsPerModify}
                onChange={(e) => {
                  const val = e.target.value.replace(',', '.');
                  setSnapsPerModify(parseFloat(val) || 0.01);
                }}
                disabled={!editing}
                className="w-full px-5 py-4 bg-white/10 backdrop-blur-xl border border-orange-500/30 rounded-xl text-white font-bold text-2xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div className="flex items-center justify-between mt-3">
                <p className="text-orange-300/70 text-xs">
                  {snapsPerModify} SNAP{snapsPerModify !== 1 ? 's' : ''}
                </p>
                <p className="text-orange-200 text-sm font-bold">
                  = €{calculatePrice(snapsPerModify)}
                </p>
              </div>
            </div>
            
            {/* 🎬 Video: Basic Chip (Grok) */}
            <div className="mb-8 p-5 bg-pink-500/10 border border-pink-500/30 rounded-xl">
              <label className="flex items-center gap-3 text-sm font-medium text-pink-300 mb-3">
                <Video className="w-5 h-5" />
                🎬 Video — Basic Chip (Grok)
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max="100"
                inputMode="decimal"
                value={snapsPerVideo}
                onChange={(e) => {
                  const val = e.target.value.replace(',', '.');
                  setSnapsPerVideo(parseFloat(val) || 0.01);
                }}
                disabled={!editing}
                className="w-full px-5 py-4 bg-white/10 backdrop-blur-xl border border-pink-500/30 rounded-xl text-white font-bold text-2xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div className="flex items-center justify-between mt-3">
                <p className="text-pink-300/70 text-xs">
                  {snapsPerVideo} SNAP{snapsPerVideo !== 1 ? 's' : ''}
                </p>
                <p className="text-pink-200 text-sm font-bold">
                  = €{calculatePrice(snapsPerVideo)}
                </p>
              </div>
            </div>
            
            {/* 🎬 Video: Kino-Modus (LTX) */}
            <div className="mb-8 p-5 bg-violet-500/10 border border-violet-500/30 rounded-xl">
              <label className="flex items-center gap-3 text-sm font-medium text-violet-300 mb-3">
                <Mic className="w-5 h-5" />
                🎬 Video — Kino-Modus (LTX)
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max="100"
                inputMode="decimal"
                value={snapsPerLipsync}
                onChange={(e) => {
                  const val = e.target.value.replace(',', '.');
                  setSnapsPerLipsync(parseFloat(val) || 0.01);
                }}
                disabled={!editing}
                className="w-full px-5 py-4 bg-white/10 backdrop-blur-xl border border-violet-500/30 rounded-xl text-white font-bold text-2xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div className="flex items-center justify-between mt-3">
                <p className="text-violet-300/70 text-xs">
                  {snapsPerLipsync} SNAP{snapsPerLipsync !== 1 ? 's' : ''}
                </p>
                <p className="text-violet-200 text-sm font-bold">
                  = €{calculatePrice(snapsPerLipsync)}
                </p>
              </div>
            </div>

            {/* Save Button */}
            {editing && (
              <button
                onClick={savePricing}
                disabled={saving}
                className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all ${
                  saving
                    ? 'bg-white/10 text-white/40 cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:shadow-xl hover:shadow-green-500/40 hover:scale-105'
                }`}
              >
              {saving ? (
                <><RefreshCw className="w-5 h-5 animate-spin" /> Speichere...</>
              ) : (
                <><Save className="w-5 h-5" /> Änderungen speichern</>
              )}
              </button>
            )}
          </div>
          
          {/* RIGHT: Live Preview */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Live Vorschau</h2>
              <Eye className="w-6 h-6 text-green-400" />
            </div>
            
            {/* Preview Cards */}
            <div className="space-y-4">
              
              {/* Training */}
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <GraduationCap className="w-6 h-6 text-purple-400" />
                    <div>
                      <p className="text-white font-semibold">1× Training</p>
                      <p className="text-purple-300/70 text-sm">{snapsPerTraining} SNAP{snapsPerTraining !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-purple-400 font-bold text-2xl">€{calculatePrice(snapsPerTraining)}</p>
                  </div>
                </div>
              </div>
              
              {/* Shot Examples */}
              {[1, 2, 4, 10].map(imageCount => {
                const totalSnaps = imageCount * snapsPerShot;
                return (
                  <div key={`shot-${imageCount}`} className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Image className="w-6 h-6 text-green-400" />
                        <div>
                          <p className="text-white font-semibold">{imageCount}× Shot{imageCount > 1 ? 's' : ''}</p>
                          <p className="text-green-300/70 text-sm">{totalSnaps.toFixed(1)} SNAP{totalSnaps !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-bold text-2xl">€{calculatePrice(totalSnaps)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Remix Example */}
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-6 h-6 text-cyan-400" />
                    <div>
                      <p className="text-white font-semibold">1× Remix</p>
                      <p className="text-cyan-300/70 text-sm">{snapsPerRemix} SNAP{snapsPerRemix !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-cyan-400 font-bold text-2xl">€{calculatePrice(snapsPerRemix)}</p>
                  </div>
                </div>
              </div>
              
              {/* Modify Example */}
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Palette className="w-6 h-6 text-orange-400" />
                    <div>
                      <p className="text-white font-semibold">1× Modify</p>
                      <p className="text-orange-300/70 text-sm">{snapsPerModify} SNAP{snapsPerModify !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-orange-400 font-bold text-2xl">€{calculatePrice(snapsPerModify)}</p>
                  </div>
                </div>
              </div>
              
              {/* 🎬 Video Example - NEU! */}
              <div className="bg-pink-500/10 border border-pink-500/30 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Video className="w-6 h-6 text-pink-400" />
                    <div>
                      <p className="text-white font-semibold">1× Video</p>
                      <p className="text-pink-300/70 text-sm">{snapsPerVideo} SNAP{snapsPerVideo !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-pink-400 font-bold text-2xl">€{calculatePrice(snapsPerVideo)}</p>
                  </div>
                </div>
              </div>

              {/* 🎙️ Lipsync Example - NEU! */}
              <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mic className="w-6 h-6 text-violet-400" />
                    <div>
                      <p className="text-white font-semibold">1× Lipsync</p>
                      <p className="text-violet-300/70 text-sm">{snapsPerLipsync} SNAP{snapsPerLipsync !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-violet-400 font-bold text-2xl">€{calculatePrice(snapsPerLipsync)}</p>
                  </div>
                </div>
              </div>

            </div>
            
            {/* Info Box */}
            <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <p className="text-blue-300 text-sm font-semibold mb-2">💡 Hinweise</p>
              <ul className="text-blue-200/80 text-xs space-y-1">
                <li>• Dezimalwerte erlaubt (z.B. 0.4, 0.5, 0.75)</li>
                <li>• Preise unter 1 € jetzt möglich</li>
                <li>• Avatar = Vehicle (globale Preise)</li>
              </ul>
            </div>
          </div>
          
        </div>
        
      </div>
    </div>
  );
}
